import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  DollarSign,
  Wallet,
  TrendingUp,
  CheckCircle2,
  CalendarDays,
  Loader2,
  AlertCircle,
  Users,
  Clock3,
  ReceiptText,
  FileText
} from 'lucide-react';


function formatMoney(value) {
  return Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
}


function competenciaAtual() {
  const agora = new Date();

  const ano = agora.getFullYear();
  const mes = String(agora.getMonth() + 1).padStart(2, '0');

  return `${ano}-${mes}`;
}


function competenciaParaInput(valor) {
  return valor || competenciaAtual();
}


function nomeCompetencia(valor) {
  if (!valor || !valor.includes('-')) {
    return valor || '-';
  }

  const [ano, mes] = valor.split('-');

  const nomes = [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro'
  ];

  const indice = Number(mes) - 1;

  return `${nomes[indice] || mes} de ${ano}`;
}


function Indicador({
  titulo,
  valor,
  descricao,
  icon: Icon,
  destaque = false
}) {

  return (
    <div
      className={[
        'bg-white border rounded-2xl p-5 sm:p-6',
        destaque
          ? 'border-slate-300 shadow-sm'
          : 'border-slate-200'
      ].join(' ')}
    >

      <div className="flex items-start justify-between gap-4">

        <div className="min-w-0">

          <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
            {titulo}
          </p>

          <p className="text-2xl sm:text-3xl font-bold text-slate-950 mt-3">
            {formatMoney(valor)}
          </p>

          <p className="text-xs sm:text-sm text-slate-500 mt-2 leading-relaxed">
            {descricao}
          </p>

        </div>


        <div
          className={[
            'w-11 h-11 rounded-xl flex items-center justify-center shrink-0',
            destaque
              ? 'bg-slate-900'
              : 'bg-slate-100'
          ].join(' ')}
        >

          <Icon
            className={[
              'w-5 h-5',
              destaque
                ? 'text-white'
                : 'text-slate-600'
            ].join(' ')}
          />

        </div>

      </div>

    </div>
  );
}


export default function ParceiroFinanceiroPage() {

  const [competencia, setCompetencia] = useState(
    competenciaAtual()
  );

  const [dados, setDados] = useState(null);

  const [loading, setLoading] = useState(true);

  const [erro, setErro] = useState('');

  const [repasses, setRepasses] = useState([]);

  const [
    loadingRepasses,
    setLoadingRepasses
  ] = useState(true);

  const [
    confirmandoRepasse,
    setConfirmandoRepasse
  ] = useState(null);

  const [
    abrindoComprovante,
    setAbrindoComprovante
  ] = useState(null);


  const carregarFinanceiro = useCallback(async () => {

    setLoading(true);
    setErro('');

    try {

      const response = await api.get(
        '/parceiro/financeiro',
        {
          params: {
            competencia
          }
        }
      );

      setDados(response.data || null);

    } catch (error) {

      console.error(error);

      setDados(null);

      setErro(
        error?.response?.data?.detail ||
        'Não foi possível carregar os dados financeiros.'
      );

    } finally {

      setLoading(false);

    }

  }, [competencia]);


  const carregarRepasses = useCallback(async () => {

    setLoadingRepasses(true);

    try {

      const response = await api.get(
        '/parceiro/repasses',
        {
          params: {
            competencia
          }
        }
      );

      const itens = Array.isArray(
        response.data?.repasses
      )
        ? response.data.repasses
        : [];

      setRepasses(itens);

    } catch (error) {

      console.error(error);
      setRepasses([]);

    } finally {

      setLoadingRepasses(false);

    }

  }, [competencia]);


  useEffect(() => {

    carregarFinanceiro();
    carregarRepasses();

  }, [
    carregarFinanceiro,
    carregarRepasses
  ]);


  const visualizarComprovante = async (
    repasse
  ) => {

    if (!repasse?.id) {
      return;
    }

    setAbrindoComprovante(repasse.id);

    try {

      const response = await api.get(
        `/parceiro/repasses/${repasse.id}/comprovante`,
        {
          responseType: 'blob'
        }
      );

      const tipo =
        response.headers?.['content-type']
        || repasse.comprovante_tipo
        || 'application/octet-stream';

      const blob = new Blob(
        [response.data],
        {
          type: tipo
        }
      );

      const url = window.URL.createObjectURL(blob);

      window.open(
        url,
        '_blank',
        'noopener,noreferrer'
      );

      window.setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 60000);

    } catch (error) {

      console.error(error);

      window.alert(
        error?.response?.data?.detail
        || 'Não foi possível abrir o comprovante.'
      );

    } finally {

      setAbrindoComprovante(null);

    }

  };


  const confirmarRecebimento = async (
    repasse
  ) => {

    if (!repasse?.id) {
      return;
    }

    const confirmar = window.confirm(
      `Confirmar o recebimento de ${formatMoney(repasse.valor)}?`
    );

    if (!confirmar) {
      return;
    }

    setConfirmandoRepasse(repasse.id);

    try {

      await api.put(
        `/parceiro/repasses/${repasse.id}/confirmar`
      );

      await Promise.all([
        carregarFinanceiro(),
        carregarRepasses()
      ]);

    } catch (error) {

      console.error(error);

      window.alert(
        error?.response?.data?.detail
        || 'Não foi possível confirmar o recebimento.'
      );

    } finally {

      setConfirmandoRepasse(null);

    }

  };


  const valorAguardando = repasses
    .filter(
      item =>
        item.status ===
        'aguardando_confirmacao'
    )
    .reduce(
      (total, item) =>
        total + Number(item.valor || 0),
      0
    );


  const valorConfirmado = repasses
    .filter(
      item =>
        item.status === 'confirmado'
    )
    .reduce(
      (total, item) =>
        total + Number(item.valor || 0),
      0
    );


  const valorDevido = Number(
    dados?.valor_destinar_parceiro || 0
  );


  const saldoReceber = Math.max(
    valorDevido
    - valorAguardando
    - valorConfirmado,
    0
  );


  return (

    <div className="space-y-7">

      {/* CABECALHO */}

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">

        <div>

          <p className="text-xs font-bold tracking-[0.18em] uppercase text-slate-400">
            Portal do Parceiro
          </p>

          <h1 className="text-2xl sm:text-3xl font-bold text-slate-950 mt-1">
            Financeiro
          </h1>

          <p className="text-sm sm:text-base text-slate-500 mt-2 max-w-2xl">
            Acompanhe a previsão, os recebimentos e o valor correspondente à sua parceria.
          </p>

        </div>


        <div className="w-full sm:w-auto">

          <label
            htmlFor="competencia-parceiro"
            className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2"
          >
            Competência
          </label>

          <div className="relative">

            <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />

            <input
              id="competencia-parceiro"
              type="month"
              value={competenciaParaInput(competencia)}
              onChange={(e) => setCompetencia(e.target.value)}
              className="h-11 w-full sm:w-[210px] rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-400"
            />

          </div>

        </div>

      </div>


      {/* ERRO */}

      {erro && (

        <div className="bg-white border border-red-200 rounded-2xl p-5">

          <div className="flex gap-3">

            <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />

            <div>

              <p className="text-sm font-semibold text-slate-900">
                Não foi possível carregar
              </p>

              <p className="text-sm text-slate-500 mt-1">
                {erro}
              </p>

              <Button
                onClick={carregarFinanceiro}
                className="mt-4 bg-slate-900 text-white hover:bg-slate-800"
              >
                Tentar novamente
              </Button>

            </div>

          </div>

        </div>

      )}


      {/* LOADING */}

      {loading && !erro && (

        <div className="min-h-[360px] flex items-center justify-center">

          <div className="flex items-center gap-3 text-slate-500">

            <Loader2 className="w-5 h-5 animate-spin" />

            <span className="text-sm">
              Carregando financeiro...
            </span>

          </div>

        </div>

      )}


      {/* CONTEUDO */}

      {!loading && !erro && dados && (

        <>

          {/* IDENTIFICACAO DA PARCERIA */}

          <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6">

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

              <div>

                <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
                  Parceria
                </p>

                <h2 className="text-xl font-bold text-slate-950 mt-1">
                  {dados.parceria || 'Minha parceria'}
                </h2>

                <p className="text-sm text-slate-500 mt-1">
                  {nomeCompetencia(dados.competencia || competencia)}
                </p>

              </div>


              <div className="flex flex-wrap gap-3">

                <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-50 border border-slate-200">

                  <TrendingUp className="w-4 h-4 text-slate-500" />

                  <span className="text-sm text-slate-600">
                    Percentual
                  </span>

                  <strong className="text-sm text-slate-950">
                    {Number(dados.percentual || 0).toLocaleString('pt-BR')}%
                  </strong>

                </div>


                <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-50 border border-slate-200">

                  <Users className="w-4 h-4 text-slate-500" />

                  <span className="text-sm text-slate-600">
                    Alunos
                  </span>

                  <strong className="text-sm text-slate-950">
                    {Number(dados.quantidade_alunos || 0)}
                  </strong>

                </div>

              </div>

            </div>

          </div>


          {/* CARDS */}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">

            <Indicador
              titulo="Previsão de recebimento"
              valor={dados.previsao_recebimento}
              descricao="Total previsto das mensalidades vinculadas às suas turmas."
              icon={DollarSign}
            />


            <Indicador
              titulo="Previsão do parceiro"
              valor={dados.previsao_parceiro}
              descricao="Valor previsto considerando o percentual da parceria."
              icon={TrendingUp}
            />


            <Indicador
              titulo="Valor recebido"
              valor={dados.valor_recebido}
              descricao="Total das mensalidades já registradas como pagas."
              icon={CheckCircle2}
            />


            <Indicador
              titulo="Valor destinado ao parceiro"
              valor={dados.valor_destinar_parceiro}
              descricao="Valor correspondente ao seu percentual sobre o que já foi recebido."
              icon={Wallet}
              destaque
            />

          </div>


          {/* REPASSES */}

          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">

            <div className="p-5 sm:p-6 border-b border-slate-200">

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

                <div>

                  <div className="flex items-center gap-2">

                    <ReceiptText className="w-5 h-5 text-slate-700" />

                    <h3 className="text-lg font-bold text-slate-950">
                      Repasses da parceria
                    </h3>

                  </div>

                  <p className="text-sm text-slate-500 mt-2">
                    Acompanhe os depósitos enviados pelo gestor e confirme quando o valor for recebido.
                  </p>

                </div>

                <div className="text-sm text-slate-500">
                  {nomeCompetencia(competencia)}
                </div>

              </div>


              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">

                <div className="rounded-xl bg-amber-50 border border-amber-100 p-4">

                  <div className="flex items-center gap-2 text-amber-700">

                    <Clock3 className="w-4 h-4" />

                    <span className="text-xs font-bold uppercase tracking-wider">
                      Aguardando confirmação
                    </span>

                  </div>

                  <p className="text-xl font-bold text-slate-950 mt-2">
                    {formatMoney(valorAguardando)}
                  </p>

                </div>


                <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">

                  <div className="flex items-center gap-2 text-emerald-700">

                    <CheckCircle2 className="w-4 h-4" />

                    <span className="text-xs font-bold uppercase tracking-wider">
                      Confirmado
                    </span>

                  </div>

                  <p className="text-xl font-bold text-slate-950 mt-2">
                    {formatMoney(valorConfirmado)}
                  </p>

                </div>


                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">

                  <div className="flex items-center gap-2 text-slate-600">

                    <Wallet className="w-4 h-4" />

                    <span className="text-xs font-bold uppercase tracking-wider">
                      Saldo a receber
                    </span>

                  </div>

                  <p className="text-xl font-bold text-slate-950 mt-2">
                    {formatMoney(saldoReceber)}
                  </p>

                </div>

              </div>

            </div>


            {loadingRepasses ? (

              <div className="py-10 flex items-center justify-center gap-2 text-slate-500">

                <Loader2 className="w-4 h-4 animate-spin" />

                <span className="text-sm">
                  Carregando repasses...
                </span>

              </div>

            ) : repasses.length === 0 ? (

              <div className="px-5 py-10 text-center">

                <ReceiptText className="w-8 h-8 text-slate-300 mx-auto" />

                <p className="text-sm font-semibold text-slate-700 mt-3">
                  Nenhum repasse nesta competência
                </p>

                <p className="text-sm text-slate-500 mt-1">
                  Quando o gestor registrar um depósito, ele aparecerá aqui.
                </p>

              </div>

            ) : (

              <div className="divide-y divide-slate-100">

                {repasses.map(repasse => {

                  const confirmado =
                    repasse.status ===
                    'confirmado';

                  const aguardando =
                    repasse.status ===
                    'aguardando_confirmacao';

                  return (

                    <div
                      key={repasse.id}
                      className="p-5 sm:p-6"
                    >

                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

                        <div>

                          <div className="flex flex-wrap items-center gap-3">

                            <p className="text-xl font-bold text-slate-950">
                              {formatMoney(repasse.valor)}
                            </p>

                            <span
                              className={[
                                'text-xs font-bold px-2.5 py-1 rounded-full',
                                confirmado
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : aguardando
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-slate-100 text-slate-600'
                              ].join(' ')}
                            >
                              {
                                confirmado
                                  ? 'Confirmado'
                                  : aguardando
                                    ? 'Aguardando confirmação'
                                    : repasse.status
                              }
                            </span>

                          </div>

                          <p className="text-sm text-slate-500 mt-2">
                            Depósito em{' '}
                            {
                              repasse.data_deposito
                              || 'data não informada'
                            }
                          </p>

                          {repasse.observacao && (

                            <p className="text-sm text-slate-500 mt-1">
                              {repasse.observacao}
                            </p>

                          )}

                          {
                            confirmado
                            && repasse.confirmado_em
                            && (

                              <div className="flex items-center gap-1.5 text-xs text-emerald-600 mt-2">

                                <CheckCircle2 className="w-3.5 h-3.5" />

                                Recebimento confirmado

                              </div>

                            )
                          }

                        </div>


                        <div className="
                          flex flex-col sm:flex-row
                          gap-2 shrink-0
                        ">

                          {repasse.tem_comprovante && (

                            <Button
                              type="button"
                              variant="outline"
                              onClick={() =>
                                visualizarComprovante(
                                  repasse
                                )
                              }
                              disabled={
                                abrindoComprovante ===
                                repasse.id
                              }
                              className="
                                border-slate-300
                                text-slate-700
                                hover:bg-slate-50
                              "
                            >

                              {
                                abrindoComprovante ===
                                repasse.id
                                  ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  )
                                  : (
                                    <FileText className="w-4 h-4 mr-2" />
                                  )
                              }

                              Ver comprovante

                            </Button>

                          )}

                          {aguardando && (

                            <Button
                              type="button"
                              onClick={() =>
                                confirmarRecebimento(
                                  repasse
                                )
                              }
                              disabled={
                                confirmandoRepasse ===
                                repasse.id
                              }
                              className="bg-slate-900 text-white hover:bg-slate-800"
                            >

                              {
                                confirmandoRepasse ===
                                repasse.id
                                  ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  )
                                  : (
                                    <CheckCircle2 className="w-4 h-4 mr-2" />
                                  )
                              }

                              Confirmar recebimento

                            </Button>

                          )}

                        </div>

                      </div>

                    </div>

                  );

                })}

              </div>

            )}

          </div>


          {/* EXPLICACAO */}

          <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6">

            <h3 className="text-sm font-bold text-slate-900">
              Como os valores são calculados
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">

              <div>

                <p className="text-xs font-semibold text-slate-700">
                  Previsão
                </p>

                <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                  Considera as mensalidades da competência vinculadas às turmas da parceria, exceto mensalidades canceladas.
                </p>

              </div>


              <div>

                <p className="text-xs font-semibold text-slate-700">
                  Recebido
                </p>

                <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                  Considera apenas as mensalidades registradas como pagas na competência selecionada.
                </p>

              </div>

            </div>

          </div>

        </>

      )}

    </div>
  );
}
